import { formatCurrency } from 'common/FormatUtilities';
import { DateString, isoDateToUrlDate } from 'common/UrlDate';
import { useCallback } from 'react';
import { CartActionLocation, CartFoodItem } from 'src/store/cart/types';
import { Portion } from '../../../Portion';
import { MoreFoodItemType } from '../../types';
import { Container, FoodName, FoodNameContainer, Image, ImageContainer, PortionContainer, Price } from './styles';

const DEFAULT_IMAGE = '';

const IMAGE_PROPS = {
  alt: 'More Food Item Image',
  sourceWidth: 220,
  sourceHeight: 220,
};

type MoreFoodItemProps = {
  deliveryDate: DateString;
  foodItem: MoreFoodItemType;
  numberOfMoreFoodItems?: number;
  quantity: number;
  onChangeFoodItemQuantity: (
    foodItem: CartFoodItem,
    quantity: number,
    deliveryDate: string,
    location: CartActionLocation
  ) => void;
  onClick?: () => void;
  location: CartActionLocation;
  className?: string;
};

export const MoreFoodItem = ({
  deliveryDate,
  foodItem,
  numberOfMoreFoodItems,
  quantity,
  onChangeFoodItemQuantity,
  location,
  className,
  onClick,
}: MoreFoodItemProps) => {
  const selectedAvailability = foodItem.availability.find(
    (date) => isoDateToUrlDate(date.availabilityDate) === deliveryDate
  );
  const numAvailable = selectedAvailability?.numAvailable ?? 0;

  const incrementQuantity = useCallback(() => {
    onChangeFoodItemQuantity({ ...foodItem, id: foodItem.foodItemId }, 1, deliveryDate, location);
  }, [foodItem, deliveryDate, onChangeFoodItemQuantity, location]);
  const decrementQuantity = useCallback(
    () => onChangeFoodItemQuantity({ ...foodItem, id: foodItem.foodItemId }, -1, deliveryDate, location),
    [foodItem, deliveryDate, onChangeFoodItemQuantity, location]
  );

  return (
    <Container numberOfMoreFoodItems={numberOfMoreFoodItems} className={className} onClick={onClick}>
      <ImageContainer>
        <Image
          src={foodItem.imageUrl ?? DEFAULT_IMAGE}
          alt={foodItem.name ?? IMAGE_PROPS.alt}
          sourceWidth={IMAGE_PROPS.sourceWidth}
          sourceHeight={IMAGE_PROPS.sourceHeight}
        />
        <PortionContainer>
          <Portion
            autoHide
            compact
            incrementQuantity={incrementQuantity}
            decrementQuantity={decrementQuantity}
            quantity={quantity}
            minQuantity={0}
            maxQuantity={numAvailable}
            minimizeOnZeroQuantity
            preventDefaultOnClick
          />
        </PortionContainer>
      </ImageContainer>
      <FoodNameContainer>
        <FoodName>{foodItem.name}</FoodName>
        <Price>{formatCurrency(foodItem.price)}</Price>
      </FoodNameContainer>
    </Container>
  );
};
